import { useCallback, useEffect, useRef, useState } from "react";
import api, { installationMessage } from "services/installation-api";
import { calibrationPayload, calibrationError } from "./calibration";

// Interim save path: the reviewed calibration is written to the existing
// device configuration endpoint after the claim completes. The pending
// request is stored in this browser under its own key so clearing the
// installation draft cannot lose it. No tokens or readings are stored here.

function keyFor(claimCode) {
  return `rivio_install_calibration:${claimCode}`;
}

function validId(value) {
  return Number.isSafeInteger(value) && value > 0;
}

function validateJob(value) {
  if (value?.version !== 1 || !validId(value.claimId) ||
      !validId(value.deviceId) || !validId(value.assetId) ||
      !["pending", "saved"].includes(value.status) ||
      !value.payload || calibrationError({ ...value.payload, enabled: true })) {
    throw new Error("Saved calibration request is invalid. Contact support before retrying.");
  }
  return value;
}

function persist(claimCode, value) {
  try {
    localStorage.setItem(keyFor(claimCode), JSON.stringify(value));
  } catch {
    throw new Error("Cannot store the pending calibration. Enable browser storage before confirming.");
  }
}

function saveMessage(error) {
  if (error?.code === "FORBIDDEN") {
    return "Your account cannot write this device calibration. An administrator must verify entity access and your contact's write grant on the installed asset.";
  }
  const details = Array.isArray(error?.errors)
    ? error.errors.map((row) => row.message || String(row)).join(" ")
    : "";
  return [installationMessage(error), details].filter(Boolean).join(" ");
}

export default function useCalibrationSave(claimCode) {
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);

  useEffect(() => {
    setJob(null);
    setError("");
    try {
      const raw = localStorage.getItem(keyFor(claimCode));
      if (raw) setJob(validateJob(JSON.parse(raw)));
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : "Could not read the pending calibration.");
    }
  }, [claimCode]);

  const prepare = useCallback(({ claimId, deviceId, assetId, value }) => {
    const payload = calibrationPayload(value);
    if (!payload) {
      try {
        localStorage.removeItem(keyFor(claimCode));
      } catch {
        throw new Error("Cannot clear the old pending calibration. Enable browser storage before confirming.");
      }
      setJob(null);
      setError("");
      return null;
    }
    const next = validateJob({
      version: 1,
      claimId: Number(claimId),
      deviceId: Number(deviceId),
      assetId: Number(assetId),
      payload,
      status: "pending",
    });
    persist(claimCode, next);
    setJob(next);
    setError("");
    return next;
  }, [claimCode]);

  const save = useCallback(async (request) => {
    if (!request || request.status === "saved" || lock.current) return;
    lock.current = true;
    setSaving(true);
    setError("");
    try {
      validateJob(request);
      const status = await api.status(request.claimId);
      if (status?.status !== "completed" ||
          Number(status.device_id) !== request.deviceId ||
          Number(status.installed_address_item_id) !== request.assetId) {
        throw new Error("The claim is not completed for this device and asset. Calibration was not sent.");
      }
      const response = await api.saveLevelCalibration(request.deviceId, request.payload);
      const matches = ["sensor_mount_type", "sensor_height_offset", "scale_min_eng", "scale_max_eng"]
        .every((field) => field === "sensor_mount_type"
          ? response[field] === request.payload[field]
          : Number(response[field]) === request.payload[field]);
      if (!matches || Number(response.device_id) !== request.deviceId ||
          response.scale_unit !== "in") {
        throw new Error("The calibration response did not match the request. Verify the device configuration before retrying.");
      }
      const next = { ...request, status: "saved" };
      persist(claimCode, next);
      setJob(next);
    } catch (problem) {
      setError(saveMessage(problem));
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }, [claimCode]);

  return { job, error, saving, prepare, save };
}
